import React, { useState, useEffect, useCallback } from 'react';
import { getEnabledPlatforms } from '../../utils/accountSettingPlatformList';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey,
  faPlug,
  faCheck,
  faExclamationTriangle,
  faSpinner,
  faTrash,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import { useAccountSettings } from '../../hooks/useAccountSettings';

// All possible OAuth providers
const ALL_OAUTH_PROVIDERS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'amazon-dsp', label: 'Amazon DSP' },
  { value: 'google-ads', label: 'Google Ads' },
  { value: 'universal-ads', label: 'Universal Ads' }
];

// Every google-ads* channel type offers the OAuth option (the channel-type split
// copied one field set across all of them), so the picker must match them all —
// not just the base type. Mirrors GOOGLE_ADS_CHANNEL_TYPES in
// services/googleAdsYamlConfig.js on the backend.
const GOOGLE_ADS_CHANNEL_TYPES = [
  'google-ads',
  'google-ads-search',
  'google-ads-display',
  'google-ads-pmax',
  'google-ads-demandgen'
];

function OAuthManagement({ userId, accountId, user, refreshTrigger, onRefresh }) {
  const [channels, setChannels] = useState([]);
  const [oauthTokens, setOauthTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [connectingChannel, setConnectingChannel] = useState(null);
  const [refreshingToken, setRefreshingToken] = useState(null);

  // Filter providers by account settings (oauth-enabled)
  const { settings: accountSettings } = useAccountSettings(accountId, user);
  const oauthEnabledPlatforms = getEnabledPlatforms(accountSettings?.['oauth-enabled']);
  const availableProviders = oauthEnabledPlatforms.length > 0
    ? ALL_OAUTH_PROVIDERS.filter(p => oauthEnabledPlatforms.includes(p.value))
    : ALL_OAUTH_PROVIDERS;

  const [selectedProvider, setSelectedProvider] = useState(availableProviders[0]?.value || 'facebook');

  const location = useLocation();
  const navigate = useNavigate();

  // Handle OAuth callback results from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthSuccess = params.get('oauth_success');
    const oauthError = params.get('oauth_error');
    const oauthErrorDescription = params.get('oauth_error_description');
    const channelId = params.get('channel_id');
    const oauthProvider = params.get('oauth_provider');

    if (oauthSuccess === 'true') {
      setSuccessMessage(`Successfully connected ${oauthProvider || 'OAuth'} for channel!`);
      // Clear URL params
      navigate('/oauth', { replace: true });
      // Refresh data
      if (onRefresh) onRefresh();
    } else if (oauthError) {
      setError(`OAuth Error: ${oauthErrorDescription || oauthError}`);
      navigate('/oauth', { replace: true });
    }
  }, [location.search, navigate, onRefresh]);

  // Fetch channels and OAuth tokens
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      // Build params for tokens request - include account_id if provided
      const tokenParams = { userId, provider: selectedProvider };
      if (accountId) {
        tokenParams.account_id = accountId;
      }

      const [channelsRes, tokensRes] = await Promise.all([
        // If accountId is provided, fetch account-channel associations
        // Otherwise, fall back to fetching all channels (for backward compatibility)
        accountId
          ? api.get(getApiUrl('/api/channels/accounts'), { params: { userId } })
          : api.get(getApiUrl('/api/channels'), { params: { userId } }),
        api.get(getApiUrl('/api/oauth/tokens'), { params: tokenParams })
      ]);

      let channelsToFilter = channelsRes.data;

      // If we fetched account-channel associations, filter by accountId
      if (accountId) {
        channelsToFilter = channelsRes.data
          .filter(ac => ac.account_id === accountId && ac.is_enabled)
          .map(ac => ({
            id: ac.channel_id,
            name: ac.channel_name,
            channel_type_name: ac.channel_type_name,
            field_values: ac.field_values || {}
          }));
      }

      // Filter to only show channels that have oauth field set to true
      // and matching the selected provider type
      const oauthSupportedChannels = channelsToFilter.filter(ch => {
        const channelType = ch.channel_type_name?.toLowerCase();
        const oauthEnabled = ch.field_values?.oauth === 'true' || ch.field_values?.oauth === true;

        // Match channel type to selected provider and check oauth field is enabled
        if (selectedProvider === 'facebook') {
          return oauthEnabled && (channelType === 'facebook' || channelType === 'fb');
        }
        // Amazon DSP is OAuth-only, no need to check the oauth field
        if (selectedProvider === 'amazon-dsp') {
          return channelType === 'amazon-dsp' || channelType === 'amazon dsp';
        }
        // Google Ads supports BOTH auth modes (hand-entered yaml or OAuth), so —
        // like Facebook — only channels that opted in via the oauth field appear.
        if (selectedProvider === 'google-ads') {
          const normalized = (channelType || '').replace(/[\s_]+/g, '-');
          return oauthEnabled && GOOGLE_ADS_CHANNEL_TYPES.includes(normalized);
        }
        // Universal Ads has no token: Connect authorizes the client's
        // organization and allows its ad accounts for this account, so every
        // universal-ads channel of the account is listed.
        if (selectedProvider === 'universal-ads') {
          return channelType === 'universal-ads';
        }
        return false;
      });

      setChannels(oauthSupportedChannels);
      setOauthTokens(tokensRes.data || []);
    } catch (err) {
      console.error('Error fetching OAuth data:', err);
      // Don't show error for tokens endpoint if it doesn't exist yet
      if (err.response?.status !== 404) {
        setError('Failed to load OAuth data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, accountId, selectedProvider]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger, selectedProvider]);

  // Sync selectedProvider when available providers change (e.g. account settings load)
  useEffect(() => {
    if (availableProviders.length > 0 && !availableProviders.find(p => p.value === selectedProvider)) {
      setSelectedProvider(availableProviders[0].value);
    }
  }, [availableProviders, selectedProvider]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  // Initiate OAuth flow for the selected provider
  const handleConnect = async (channel) => {
    try {
      setConnectingChannel(channel.id);
      setError(null);

      const response = await api.get(getApiUrl(`/api/oauth/${selectedProvider}/authorize`), {
        params: {
          channel_id: channel.id,
          user_id: userId,
          account_id: accountId
        }
      });

      // Open OAuth provider in a new tab
      window.open(response.data.authorization_url, '_blank');
      setConnectingChannel(null);
    } catch (err) {
      console.error('Error initiating OAuth:', err);
      setError('Failed to initiate OAuth: ' + (err.response?.data?.message || err.message));
      setConnectingChannel(null);
    }
  };

  // Check if a channel has an active token
  const getChannelToken = (channelId) => {
    return oauthTokens.find(
      token => token.channel_id === channelId && !token.revoked_at
        // A Universal Ads connection belongs to one account, while its
        // channel config may be shared with others.
        && (selectedProvider !== 'universal-ads' || token.account_id === accountId)
    );
  };

  // Check if token is expired or expiring soon
  const getTokenStatus = (token) => {
    if (!token) return null;
    if (!token.expires_at) return 'active';

    const expiresAt = new Date(token.expires_at);
    const now = new Date();
    const daysUntilExpiry = (expiresAt - now) / (1000 * 60 * 60 * 24);

    if (daysUntilExpiry < 0) return 'expired';

    // Amazon DSP access tokens are ~1hr and auto-refreshed by the scheduler.
    // Only flag as expiring_soon if token expires in < 5 minutes (scheduler may have failed).
    if (selectedProvider === 'amazon-dsp') {
      const minutesUntilExpiry = daysUntilExpiry * 24 * 60;
      if (minutesUntilExpiry < 5) return 'expiring_soon';
      return 'active';
    }

    if (daysUntilExpiry < 7) return 'expiring_soon';
    return 'active';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Manually refresh a token
  const handleRefreshToken = async (tokenId) => {
    try {
      setRefreshingToken(tokenId);
      setError(null);
      const response = await api.post(getApiUrl(`/api/oauth/tokens/${tokenId}/refresh`));
      setSuccessMessage(`Token refreshed! New expiry: ~${response.data.expires_in_days} days`);
      fetchData();
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError('Failed to refresh token: ' + message);
    } finally {
      setRefreshingToken(null);
    }
  };

  // Revoke a token
  const handleRevokeToken = async (tokenId) => {
    const confirmText = selectedProvider === 'universal-ads'
      ? 'Disconnect Universal Ads? Its ad accounts will no longer be allowed for launches from this account. (The app stays authorized on Universal Ads until their support removes it.)'
      : 'Are you sure you want to revoke this token?';
    if (!window.confirm(confirmText)) return;

    try {
      await api.delete(getApiUrl(`/api/oauth/tokens/${tokenId}`), {
        params: { userId }
      });
      setSuccessMessage('Token revoked successfully');
      fetchData();
    } catch (err) {
      setError('Failed to revoke token: ' + (err.response?.data?.message || err.message));
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <p>Loading OAuth connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">
          <FontAwesomeIcon icon={faKey} className="icon" />
          OAuth Connections
        </h2>
        <div className="admin-card-actions">
          <button className="btn btn-secondary" onClick={fetchData}>
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {/* Provider Filter */}
      <div className="filter-section" style={{
        marginTop: '1.5rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <label htmlFor="provider-filter" style={{ fontWeight: '500', marginBottom: 0 }}>Provider:</label>
        <select
          id="provider-filter"
          className="form-control"
          style={{ width: 'auto', minWidth: '180px' }}
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e.target.value)}
        >
          {availableProviders.map(provider => (
            <option key={provider.value} value={provider.value}>
              {provider.label}
            </option>
          ))}
        </select>
      </div>

      {channels.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faPlug} className="empty-state-icon" />
          <p className="empty-state-text">No {selectedProvider} channels with OAuth enabled</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr className="bg-primary text-white" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                <th className="py-3">Channel</th>
                <th className="py-3">Type</th>
                <th className="py-3">OAuth Status</th>
                <th className="py-3">Token Expires</th>
                <th className="py-3">Last Refreshed</th>
                <th className="py-3">Connected By</th>
                <th className="py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {channels.map(channel => {
                const token = getChannelToken(channel.id);
                const status = getTokenStatus(token);

                return (
                  <tr key={channel.id}>
                    <td>{channel.name}</td>
                    <td>
                      <span className="badge badge-info" style={{ backgroundColor: selectedProvider === 'amazon-dsp' ? '#ff9900' : '#3b5998', color: 'white' }}>
                        {channel.channel_type_name}
                      </span>
                    </td>
                    <td>
                      {status === 'active' && (
                        <span className="badge badge-success">
                          <FontAwesomeIcon icon={faCheck} /> Connected
                        </span>
                      )}
                      {status === 'expiring_soon' && (
                        <span className="badge badge-warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} /> Expiring Soon
                        </span>
                      )}
                      {status === 'expired' && (
                        <span className="badge badge-danger">
                          <FontAwesomeIcon icon={faExclamationTriangle} /> Expired
                        </span>
                      )}
                      {!token && (
                        <span className="badge badge-secondary">Not Connected</span>
                      )}
                    </td>
                    <td>{token ? formatDate(token.expires_at) : '-'}</td>
                    <td>{token?.last_refreshed_at ? formatDate(token.last_refreshed_at) : '-'}</td>
                    <td>{token?.created_by_email || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        {!token || status === 'expired' || status === 'expiring_soon' ? (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleConnect(channel)}
                            disabled={connectingChannel === channel.id}
                            title={`Connect ${selectedProvider}`}
                          >
                            {connectingChannel === channel.id ? (
                              <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                              <FontAwesomeIcon icon={faPlug} />
                            )}
                            {' '}Connect
                          </button>
                        ) : null}
                        {token && status !== 'expired' && selectedProvider !== 'universal-ads' && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleRefreshToken(token.id)}
                            disabled={refreshingToken === token.id}
                            title="Refresh Token (extend expiry)"
                          >
                            {refreshingToken === token.id ? (
                              <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                              <FontAwesomeIcon icon={faRefresh} />
                            )}
                            {' '}Refresh
                          </button>
                        )}
                        {token && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRevokeToken(token.id)}
                            title="Revoke Token"
                          >
                            <FontAwesomeIcon icon={faTrash} /> Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OAuthManagement;

